import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASK_TYPES, STATUSES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { buildTaskTypeRelationParams } from './taskTypeApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('name', values.name ?? '');
  fd.append('color', values.color ?? '');
  fd.append('description', values.description ?? '');
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

function normalizeHexInput(raw) {
  const t = String(raw || '').trim();
  if (!t) return '';
  if (t.startsWith('#')) return t.length <= 9 ? t : t.slice(0, 9);
  return `#${t.replace(/^#/, '')}`;
}

const CreateTaskType = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [description, setDescription] = useState('');
  const [record, setRecord] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setStatusLoading(true);
    try {
      let response;
      try {
        response = await api.get(STATUSES.BY_MODEL('TaskType'));
      } catch {
        try {
          response = await api.get(STATUSES.BY_MODEL('TaskType'));
        } catch {
          response = await api.get(STATUSES.LIST, { params: { paginate: 0 } });
        }
      }
      const list = extractList(response?.data);
      const arr = Array.isArray(list) ? list : [];
      setStatusOptions(
        arr.map((s) => ({
          id: s.id,
          label: statusLabel(s),
          raw: s,
        }))
      );
    } catch {
      toast.error('Could not load statuses');
      setStatusOptions([]);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(TASK_TYPES.BY_ID(editId), {
          params: buildTaskTypeRelationParams(),
        });
        const t = data?.data ?? data;
        if (cancelled || !t) return;
        setRecord(t);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load task type');
        navigate('/task-types');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  useEffect(() => {
    if (!isEdit || !record) return;

    setName(record.name ?? '');
    setColor(record.color && String(record.color).trim() ? normalizeHexInput(record.color) : '#3B82F6');
    setDescription(record.description ?? '');

    const st = record.status && typeof record.status === 'object' ? record.status : null;
    const sid = record.status_id ?? st?.id;
    if (sid) {
      if (st) {
        const opt = { id: sid, label: statusLabel(st), raw: st };
        setSelectedStatus(opt);
        setStatusOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusOptions.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
  }, [isEdit, record, statusOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStatus?.id || !name.trim()) {
      toast.warn('Select status and enter a name');
      return;
    }

    const values = {
      status_id: selectedStatus.id,
      name: name.trim(),
      color: color.trim(),
      description: description.trim(),
    };
    const fd = buildFormData(values);
    const q = new URLSearchParams(buildTaskTypeRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${TASK_TYPES.BY_ID(editId)}?${q}`, fd);
        toast.success('Task type updated');
      } else {
        await api.post(`${TASK_TYPES.LIST}?${q}`, fd);
        toast.success('Task type created');
      }
      navigate('/task-types');
    } catch (err) {
      toast.error(
        err.response?.data?.message || (isEdit ? 'Failed to update task type' : 'Failed to create task type')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const pickerValue = /^#([0-9A-Fa-f]{6})$/.test(color.trim()) ? color.trim() : '#000000';

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-types')} sx={{ mb: 2 }}>
          Back to task types
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task type' : 'Create task type'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(_, v) => setSelectedStatus(v)}
                  loading={statusLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" required fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <TextField
                    label="Color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    fullWidth
                    placeholder="#3B82F6"
                    helperText="Hex color (e.g. #3B82F6)"
                  />
                  <Box
                    component="label"
                    sx={{
                      mt: 1,
                      width: 48,
                      height: 40,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      flexShrink: 0,
                      bgcolor: pickerValue,
                      overflow: 'hidden',
                    }}
                  >
                    <input
                      type="color"
                      value={pickerValue}
                      onChange={(e) => setColor(e.target.value)}
                      style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Create task type'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/task-types')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTaskType;
